import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Navbar } from './common/Catalyst/navbar';
import AppSidebar from './AppSidebar';
import { Heading, Subheading } from './common/Catalyst/heading';
import { Divider } from './common/Catalyst/divider';
import { Button } from './common/Catalyst/button';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import BillingTabs, { BillingTab } from './billing/BillingTabs';
import BillingPolicyForm from './billing/BillingPolicyForm';
import ScheduleList from './billing/ScheduleList';
import RunList from './billing/RunList';
import RunDetail from './billing/RunDetail';
import PaymentsPanel from './billing/PaymentsPanel';
import CollectionsPanel from './billing/CollectionsPanel';
import CaseEventTrail from './billing/CaseEventTrail';
import BillingOverview from './billing/BillingOverview';
import NoticeRuleList from './billing/NoticeRuleList';
import { HoaReturn } from './clients/rust-api-client';
import { hoaService } from './clients/ApiClients';
import { useSyncedHoa } from './sync/hooks';
import { billingErrorMessage } from './utils/billingHelpers';
import { getUserTypeFromToken } from './utils/TokenUtils';
import {
  canGenerateRuns,
  canManageBillingPolicy,
  canManageSchedules,
  canPostCredit,
  canPostRun,
  canAdvanceCase,
  canRecordPayment,
  canReverseEntry,
  canRunBillingJobs,
  canWorkCase,
  canViewLedger,
} from './constants/UserTypes';

/**
 * Which screen of the HOA billing area to show.
 *
 * Billing is per-HOA policy and process — settings, dues, runs,
 * payments, collections. Per-home things (the ledger, a statement, a
 * coupon, tenures, estoppel) are the *account* and live on
 * `HomeBillingPage`.
 */
export type BillingSection =
  | 'overview'
  | 'settings'
  | 'dues'
  | 'runs'
  | 'payments'
  | 'collections';

interface Props {
  section: BillingSection;
}

const BillingPage: React.FC<Props> = ({ section }) => {
  const { hoaId, runId, caseId } = useParams<{
    hoaId: string;
    runId?: string;
    caseId?: string;
  }>();
  const navigate = useNavigate();
  const userType = useMemo(() => getUserTypeFromToken(), []);

  const [fetchedHoa, setHoa] = useState<HoaReturn | null>(null);
  // The sync engine's copy when it has this association (#561); fetched otherwise.
  const syncedHoa = useSyncedHoa(hoaId);
  const haveSynced = !!syncedHoa;
  const hoa = syncedHoa ?? fetchedHoa;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHoa = async () => {
      if (!hoaId) return;
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const { data } = await hoaService.getHoa({ id: hoaId });
        setHoa(data);
      } catch (err) {
        console.error('Error fetching HOA:', err);
        setError(billingErrorMessage(err, "Couldn't load this association."));
      } finally {
        setLoading(false);
      }
    };
    void fetchHoa();
  }, [hoaId, haveSynced]);

  const base = `/hoa/${hoaId}/billing`;
  const tabs: BillingTab[] = [
    { to: base, label: 'Overview' },
    { to: `${base}/dues`, label: 'Dues schedules' },
    { to: `${base}/runs`, label: 'Billing runs', matches: [`${base}/runs/`] },
    { to: `${base}/payments`, label: 'Payments' },
    {
      to: `${base}/collections`,
      label: 'Collections',
      matches: [`${base}/collections/`],
    },
    // Policy is Owner/Admin to write and everyone to read, so the tab is
    // shown to anyone who can read the ledger; the form inside it is
    // what goes read-only for a Manager or a BoardMember.
    { to: `${base}/settings`, label: 'Settings' },
  ];

  const body = (): React.ReactNode => {
    switch (section) {
      case 'settings':
        return hoaId ? (
          <>
            <BillingPolicyForm
              scope="hoa"
              scopeId={hoaId}
              canEdit={canManageBillingPolicy(userType)}
            />
            <Divider className="my-10" />
            <NoticeRuleList
              scope="hoa"
              scopeId={hoaId}
              canEdit={canManageBillingPolicy(userType)}
            />
          </>
        ) : null;
      case 'dues':
        return hoaId ? (
          <ScheduleList
            hoaId={hoaId}
            canManage={canManageSchedules(userType)}
          />
        ) : null;
      case 'runs':
        if (!hoaId) return null;
        return runId ? (
          <RunDetail
            hoaId={hoaId}
            runId={runId}
            canPost={canPostRun(userType)}
            canReverse={canReverseEntry(userType)}
          />
        ) : (
          <RunList hoaId={hoaId} canGenerate={canGenerateRuns(userType)} />
        );
      case 'payments':
        return hoaId ? (
          <PaymentsPanel
            hoaId={hoaId}
            canRecord={canRecordPayment(userType)}
            canRefund={canPostCredit(userType)}
            canReturn={canReverseEntry(userType)}
          />
        ) : null;
      case 'collections':
        if (!hoaId) return null;
        return caseId ? (
          <CaseEventTrail
            hoaId={hoaId}
            caseId={caseId}
            canAdvance={canAdvanceCase(userType)}
            canNote={canWorkCase(userType)}
          />
        ) : (
          <CollectionsPanel
            hoaId={hoaId}
            canOpen={canWorkCase(userType)}
            canAdvance={canAdvanceCase(userType)}
          />
        );
      case 'overview':
      default:
        return hoaId ? (
          <BillingOverview
            hoaId={hoaId}
            canRunJobs={canRunBillingJobs(userType)}
          />
        ) : null;
    }
  };

  if (!canViewLedger(userType)) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="hoas" />}
        navbar={<Navbar>{/* Your navbar content */}</Navbar>}
      >
        <Heading level={2}>Billing</Heading>
        <Subheading className="mt-2">
          You do not have access to this association's billing.
        </Subheading>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="hoas" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex justify-between items-center">
        <div>
          <Heading level={2}>
            {hoa ? `${hoa.name} — Billing` : 'Billing'}
          </Heading>
          <Subheading className="mt-1">
            Dues, payments and collections for this association.
          </Subheading>
        </div>
        {hoaId && (
          <Button plain onClick={() => navigate(`/hoa/${hoaId}`)}>
            Back to the association
          </Button>
        )}
      </div>

      <Divider className="mt-4 mb-6" />

      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load this association"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <BillingTabs tabs={tabs} />

      {loading ? <LoadingSpinner /> : body()}
    </SidebarLayout>
  );
};

export default BillingPage;
